import {
  useSelectedValueAttributeByKey,
  useSelectedValueAttributesByKeys,
  useSelectedValueByVariableKey,
  useValueByKey,
  useVariableByKey,
  useVariables,
  Value,
  VariableT,
} from '@k3/core'
import React, { useContext } from 'react'
import { ColorRepresentation, Euler, Vector3 } from 'three'
import { degToRad } from 'three/src/math/MathUtils'
import { Helper } from '../../classes/helper'
import { isForbiddenValues } from './isForbiddenValues'
import { useMeasures } from './useMeasures'
import { useRoof } from './useRoof'
import { Keys } from './utlility/utils'

export type SidewallContextType = {
  id?: string
  type?: (typeof Keys.Seitenwände.Values)[keyof typeof Keys.Seitenwände.Values]['key']
  index?: number
} & {
  // key: VariableT['key']
  [key: string]: Value
}

function getAttribute(
  variables: VariableT[],
  sidewallContext: SidewallContextType,
  variableKey: string,
  attributeKey: string
) {
  const variable = variables.find(
    (v) => v.id === sidewallContext[variableKey]?.variableId
  )

  const attribute = variable?.attrs?.find((a) => a.key === attributeKey)
  const attributeValue = sidewallContext[variableKey]?.attrs?.find(
    (a) => a.attrId === attribute?.id
  )?.value

  return attributeValue
}

function getWedgeAttributes(
  variables: VariableT[],
  sidewallContext: SidewallContextType,
  variableKey: string,
  attributeKey: string
) {
  const variable = variables.find(
    (v) => v.id === sidewallContext[variableKey]?.variableId
  )

  const attribute = variable?.attrs?.find((a) => a.key === attributeKey)

  const test = useValueByKey('keil')

  const attributeValues = test?.attrs?.find(
    (a) => a.attrId === attribute?.id
  )?.value

  return attributeValues
}

export type SidewallResultContextType = ReturnType<
  typeof useSidewallResultContext
>

export const SidewallContext = React.createContext<SidewallContextType>({})
export const SidewallResultContext =
  React.createContext<SidewallResultContextType>(
    {} as SidewallResultContextType
  )

export const useSidewall = () => {
  const context = useContext(SidewallResultContext)

  return context
}

export const useSidewallResultContext = (
  sidewallContext: SidewallContextType
) => {
  const variables = useVariables()
  const {
    depth,
    eaves,
    eavesWidth,
    verandaTyp,
    userWidth,
    polesNumber,
    polesNumberBackside,
    attachment,
    userAngle,
    userHeight,
    frontHeight,
    eavesHeight,
    offsetRoofLog,
    staticLogPosition,
    hasStaticLog,
    indentBar,
    isQubus,
    hasBacksideStaticlog,
    staticLogBackside,
  } = useMeasures()
  const { color } = useRoof()
  const sideWallValue = sidewallContext['seitenwaende']

  const sideTyp = sidewallContext[Keys.SeitenwandTyp.Variable]

  const sideWallType = sideTyp?.key

  const hasSideWallKey = useSelectedValueByVariableKey('seitenwaende')?.key
  const sideWallVariable = useVariableByKey('seitenwaende')

  const hideAlways =
    sideWallVariable?.settings?.templateOptions?.hideAlways === true
  const hasSideWall = hideAlways ? false : hasSideWallKey !== ''

  const isWallCenter =
    sidewallContext.type === Keys.Seitenwände.Values.SeitenwandMittig.key ||
    sidewallContext.type === Keys.Seitenwände.Values.SeitenwandHinten.key
      ? true
      : false

  const hasWedege =
    sidewallContext[Keys.ZusatzKeil.Variable]?.key ==
    isForbiddenValues(Keys.ZusatzKeil.Values.Ja)

  const wedge =
    sideWallType === isForbiddenValues(Keys.SeitenwandTyp.Values.Keil)
      ? true
      : hasWedege

  const hasVerticalMarquee =
    (sidewallContext['verticalMarquee']?.value as string) === '1' ? true : false
  const checkDefaultVerticalMarquee =
    (sidewallContext['verticalMarqueeInside']?.key as string) ?? true

  const verticalMarqueeInside =
    (sidewallContext['verticalMarqueeInside']?.key as string) === 'in'
      ? false
      : !hasVerticalMarquee

  const verticalMarqueeInsideString = hasVerticalMarquee
    ? 'out'
    : (sidewallContext['verticalMarqueeInside']?.key as string) ?? 'in'

  const sideWall = {
    hasWedge: wedge,
    hasWindowsWall:
      sideWallType === isForbiddenValues(Keys.SeitenwandTyp.Values.Rahmenwand)
        ? true
        : false,
    hasSlidingDoor:
      sideWallType === isForbiddenValues(Keys.SeitenwandTyp.Values.Schiebetuer)
        ? true
        : false,
    hasPlankWall:
      sideWallType === isForbiddenValues(Keys.SeitenwandTyp.Values.Plankenwand)
        ? true
        : false,
    hasMarquee:
      sideWallType ===
      isForbiddenValues(Keys.SeitenwandTyp.Values.Senkrechtmarkise)
        ? true
        : hasVerticalMarquee,
    hasSlidingFrameDoor:
      sideWallType ===
      isForbiddenValues(Keys.SeitenwandTyp.Values.RahmenSchiebetuer)
        ? true
        : false,
    hasShutters:
      sideWallType === isForbiddenValues(Keys.SeitenwandTyp.Values.Shutters)
        ? true
        : false,
    noSelection:
      sideWallType === isForbiddenValues(Keys.SeitenwandTyp.Values.Ohne)
        ? true
        : false,
  }

  const windowsWallTyp =
    sidewallContext[Keys.RahmenwandTyp.Variable]?.key === 'typ2'

  const plankWall = sidewallContext[Keys.PlankenwandObenTyp.Variable]?.key

  const plankWallWindow = {
    hasWindowsWall: plankWall === 'seitenwand' ? true : false,
    hasTyp1: plankWall === 'typ1' ? true : false,
    hasTyp2: plankWall === 'typ2' ? true : false,
  }

  const triangularDividerOld =
    (sidewallContext['keile'] as unknown as number) ?? 0
  const triangularDivider = triangularDividerOld >= 0 ? triangularDividerOld : 0

  let wedgeCut =
    ((sidewallContext['keilabschnitt'] as unknown as number) ?? 0) / 100
  wedgeCut = sideWall.hasWedge ? wedgeCut : 0

  const sideWallFrameMaxHeight = frontHeight - wedgeCut - 0.1

  const frameHeight =
    ((sidewallContext['rahmenhoehe1'] as unknown as number) ?? 0) / 100 ||
    userHeight -
      frontHeight +
      (wedge ? frontHeight - wedgeCut : frontHeight) / 2

  const windowColorWedge = {
    opacity: getAttribute(
      variables,
      sidewallContext,
      Keys.GlasAuswahlKeil.Variable,
      'opacity'
    ) as number,
    roughness: getAttribute(
      variables,
      sidewallContext,
      Keys.GlasAuswahlKeil.Variable,
      'roughness'
    ) as number,
    appearance: sidewallContext[Keys.GlasAuswahlKeil.Variable]?.key,
  }

  const frameWall = {
    frameWallTyp: sidewallContext[Keys.RahmenwandTyp.Variable]?.key,
    windowColorTop: {
      opacity: getAttribute(
        variables,
        sidewallContext,
        Keys.GlasAuswahlOben.Variable,
        'opacity'
      ) as number,
      roughness: getAttribute(
        variables,
        sidewallContext,
        Keys.GlasAuswahlOben.Variable,
        'roughness'
      ) as number,
      appearance: sidewallContext[Keys.GlasAuswahlOben.Variable]?.key,
    },
    windowColorBottom: {
      opacity: getAttribute(
        variables,
        sidewallContext,
        Keys.GlasAuswahlUnten.Variable,
        'opacity'
      ) as number,
      roughness: getAttribute(
        variables,
        sidewallContext,
        Keys.GlasAuswahlUnten.Variable,
        'roughness'
      ) as number,
      appearance: sidewallContext[Keys.GlasAuswahlUnten.Variable]?.key,
    },
    frameHeight:
      frameHeight >= sideWallFrameMaxHeight
        ? sideWallFrameMaxHeight
        : frameHeight,
  }

  const logBarDepthNumber =
    (sidewallContext['logBarDepth'] as unknown as number) / 100 || 0

  const wedgeColorSet = getAttribute(
    variables,
    sidewallContext,
    'glasWedge',
    'opacity'
  ) as number

  const wedgeColor = sideWall.hasWedge
    ? wedgeColorSet
    : windowsWallTyp
    ? frameWall.windowColorTop.opacity
    : frameWall.windowColorBottom.opacity

  // const slidingDoor = useSelectedValueByVariableKey('slidingDoor')?.label

  const brushTyp = sidewallContext[Keys.BürstenTyp.Variable]?.key === 'ja'

  const [logWidth, logDepth] = Helper.stringToNumbers(
    getAttribute(
      variables,
      sidewallContext,
      Keys.Sidewall.Variable,
      Keys.Sidewall.Attributes.Log
    ) as string
  )

  const [wedgeLogWidth, wedgeLogDepth] = Helper.stringToNumbers(
    getWedgeAttributes(
      variables,
      sidewallContext,
      Keys.Sidewall.Variable,
      Keys.Sidewall.Attributes.Log
    ) as string
  )

  const sidwallTyp = {
    log: {
      width: logWidth / 100,
      depth: logDepth / 100,
    },
    glas:
      ((getAttribute(
        variables,
        sidewallContext,
        Keys.Sidewall.Variable,
        Keys.Sidewall.Attributes.Glas
      ) ?? 0) as number) / 100,
  }

  const logBarDepth = logBarDepthNumber
    ? logBarDepthNumber
    : sidwallTyp.log.width

  const sideWallMaxHeight1 =
    (sidewallContext[Keys.Sidewall.Attributes.MaxHeight] as unknown as number) /
    100

  let spaceBetween = 0
  let basicSpaceBetween = 0

  let windowsSideTotal = 0

  let windowsSideWidth = 0
  const windowsSideMaxWidthOld =
    (sidewallContext['windowsSideWidth'] as unknown as number) / 100
  const windowsSideMaxWidth = windowsSideMaxWidthOld
    ? windowsSideMaxWidthOld
    : 0.8
  let position = new Vector3(0, 0, 0)
  let basicPosition = new Vector3(0, 0, 0)
  let rotation = new Euler(0, 0, 0)
  switch (sidewallContext.type) {
    case Keys.Seitenwände.Values.SeitenwandLinks.key: {
      spaceBetween = (depth -
        verandaTyp.log.depth -
        (attachment.free ? verandaTyp.log.depth : 0) -
        (eaves ? eavesWidth : 0)) as number

      position = new Vector3(
        -userWidth / 2 +
          sidwallTyp.log.depth / 2 +
          (sideWall.hasSlidingDoor ? sidwallTyp.log.depth / 2 : 0),
        0,
        spaceBetween / 2 + (attachment.free ? verandaTyp.log.depth : 0)
      )

      basicSpaceBetween = spaceBetween

      basicPosition = new Vector3(
        -userWidth / 2,
        0,
        (spaceBetween - verandaTyp.log.depth) / 2
      )

      windowsSideTotal = Helper.setWindowsSideTotal(
        verandaTyp.log.depth,
        spaceBetween,
        attachment.wall,
        windowsSideMaxWidth
      )

      windowsSideWidth =
        (spaceBetween - sidwallTyp.log.width) / windowsSideTotal

      break
    }
    case Keys.Seitenwände.Values.SeitenwandRechts.key: {
      spaceBetween = (depth -
        verandaTyp.log.depth -
        (attachment.free ? verandaTyp.log.depth : 0) -
        (eaves ? eavesWidth : 0)) as number

      position = new Vector3(
        userWidth / 2 -
          sidwallTyp.log.depth / 2 -
          (sideWall.hasSlidingDoor ? sidwallTyp.log.depth / 2 : 0),
        0,
        spaceBetween / 2 + (attachment.free ? verandaTyp.log.depth : 0)
      )

      basicSpaceBetween = spaceBetween

      basicPosition = new Vector3(
        userWidth / 2,
        0,
        (spaceBetween - verandaTyp.log.depth) / 2
      )

      windowsSideTotal = Helper.setWindowsSideTotal(
        verandaTyp.log.depth,
        spaceBetween,
        attachment.wall,
        windowsSideMaxWidth
      )

      windowsSideWidth =
        (spaceBetween - sidwallTyp.log.width) / windowsSideTotal

      break
    }
    case Keys.Seitenwände.Values.SeitenwandHinten.key: {
      const lichtesMaß =
        userWidth - verandaTyp.log.width * (polesNumberBackside - 1)
      spaceBetween =
        (lichtesMaß - verandaTyp.log.width) / (polesNumberBackside - 1)

      const startPosition =
        -userWidth / 2 +
        verandaTyp.log.width / 2 +
        spaceBetween / 2 +
        verandaTyp.log.width / 2
      const availableWidth = spaceBetween
      const delta = availableWidth + verandaTyp.log.width

      position = new Vector3(
        startPosition + delta * (sidewallContext.index ?? 0),
        0,
        sidwallTyp.log.depth / 2
      )

      basicSpaceBetween = spaceBetween + 0.0075

      basicPosition = new Vector3(
        -lichtesMaß / 2 +
          spaceBetween / 2 +
          verandaTyp.log.width / 2 +
          (availableWidth + verandaTyp.log.width) *
            (sidewallContext.index ?? 0),
        0,
        0
      )
      windowsSideTotal = Helper.setWindowsSideTotal(
        verandaTyp.log.width,
        spaceBetween,
        true,
        windowsSideMaxWidth
      )

      windowsSideWidth = spaceBetween / windowsSideTotal

      rotation = new Euler(0, degToRad(-90), 0)
      break
    }
    case Keys.Seitenwände.Values.SeitenwandMittig.key: {
      const lichtesMaß =
        userWidth -
        verandaTyp.log.width * polesNumber -
        indentBar.left -
        indentBar.right
      spaceBetween = lichtesMaß / (polesNumber - 1)
      const startPosition =
        -userWidth / 2 +
        indentBar.left +
        spaceBetween / 2 +
        verandaTyp.log.width
      const availableWidth = spaceBetween
      const delta = availableWidth + verandaTyp.log.width

      position = new Vector3(
        startPosition + delta * (sidewallContext.index ?? 0),
        0,
        (eaves
          ? depth - eavesWidth - verandaTyp.log.depth / 2
          : depth - sidwallTyp.log.depth / 2) as number
      )

      basicSpaceBetween = spaceBetween

      basicPosition = new Vector3(
        -lichtesMaß / 2 +
          spaceBetween / 2 -
          (verandaTyp.log.width * (polesNumber - 1)) / 2 +
          delta * (sidewallContext.index ?? 0),
        0,
        ((eaves ? depth - eavesWidth : depth) -
          sidwallTyp.log.depth / 2 -
          (sideWall.hasSlidingDoor ? sidwallTyp.log.depth / 2 : 0)) as number
      )

      windowsSideTotal = Helper.setWindowsSideTotal(
        verandaTyp.log.width,
        spaceBetween,
        true,
        windowsSideMaxWidth
      )

      windowsSideWidth = spaceBetween / windowsSideTotal

      rotation = new Euler(0, degToRad(90), 0)
      break
    }
  }

  if (isQubus) {
    switch (sidewallContext.type) {
      case Keys.Seitenwände.Values.SeitenwandLinks.key: {
        position.x -= verandaTyp.log.width / 2
        windowsSideWidth =
          (spaceBetween - sidwallTyp.log.width) / windowsSideTotal
        basicPosition = new Vector3(-userWidth / 2, 0, basicSpaceBetween / 2)

        break
      }
      case Keys.Seitenwände.Values.SeitenwandRechts.key: {
        position.x += verandaTyp.log.width / 2
        windowsSideWidth =
          (spaceBetween - sidwallTyp.log.width) / windowsSideTotal

        basicPosition = new Vector3(userWidth / 2, 0, spaceBetween / 2)
        break
      }
      case Keys.Seitenwände.Values.SeitenwandHinten.key: {
        const lichtesMaß =
          userWidth - verandaTyp.log.width * (polesNumberBackside - 1)
        spaceBetween = lichtesMaß / (polesNumberBackside - 1)

        const startPosition =
          -userWidth / 2 + spaceBetween / 2 + verandaTyp.log.width / 2
        const availableWidth = spaceBetween
        const delta = availableWidth + verandaTyp.log.width

        position = new Vector3(
          startPosition + delta * (sidewallContext.index ?? 0),
          0,
          sideWall.hasPlankWall
            ? sidwallTyp.log.depth / 2
            : sidwallTyp.log.depth
        )

        basicSpaceBetween = spaceBetween
        basicPosition = new Vector3(
          -lichtesMaß / 2 +
            spaceBetween / 2 +
            (availableWidth + verandaTyp.log.width) *
              (sidewallContext.index ?? 0),
          0,
          0
        )

        windowsSideTotal = Helper.setWindowsSideTotal(
          verandaTyp.log.width,
          spaceBetween,
          true,
          windowsSideMaxWidth
        )

        windowsSideWidth = spaceBetween / windowsSideTotal

        rotation = new Euler(0, degToRad(-90), 0)
        break
      }
      case Keys.Seitenwände.Values.SeitenwandMittig.key: {
        const lichtesMaß = userWidth - verandaTyp.log.width * (polesNumber - 1)
        spaceBetween = lichtesMaß / (polesNumber - 1)

        const startPosition =
          -userWidth / 2 + spaceBetween / 2 + verandaTyp.log.width / 2
        const availableWidth = spaceBetween
        const delta = availableWidth + verandaTyp.log.width
        position = new Vector3(
          startPosition + delta * (sidewallContext.index ?? 0),
          0,
          ((eaves ? depth - eavesWidth : depth) -
            sidwallTyp.log.depth / 2 -
            (sideWall.hasSlidingDoor ? sidwallTyp.log.depth / 2 : 0)) as number
        )

        basicSpaceBetween = spaceBetween
        basicPosition = new Vector3(
          -lichtesMaß / 2 +
            spaceBetween / 2 -
            (verandaTyp.log.width * (polesNumber - 1)) / 2 +
            delta * (sidewallContext.index ?? 0),
          0,
          ((eaves ? depth - eavesWidth : depth) -
            sidwallTyp.log.depth / 2 -
            (sideWall.hasSlidingDoor ? sidwallTyp.log.depth / 2 : 0)) as number
        )

        windowsSideTotal = Helper.setWindowsSideTotal(
          verandaTyp.log.width,
          spaceBetween,
          true,
          windowsSideMaxWidth
        )

        windowsSideWidth = spaceBetween / windowsSideTotal

        rotation = new Euler(0, degToRad(90), 0)
        break
      }
    }
  }
  const sidewallLeftRight = !isWallCenter

  const height1 = Helper.calculateTriangleSideLength(
    userAngle,
    90,
    depth - verandaTyp.log.depth
  ).a
  const height2 = Math.tan((userAngle * Math.PI) / 180) * spaceBetween

  const spaceY =
    eaves &&
    sidewallContext.type !== Keys.Seitenwände.Values.SeitenwandHinten.key
      ? sidewallContext.type !== Keys.Seitenwände.Values.SeitenwandMittig.key
        ? userHeight - (height1 - wedgeCut)
        : eavesHeight
      : !isWallCenter
      ? frontHeight + sidwallTyp.log.width / 2
      : sidewallContext.type === Keys.Seitenwände.Values.SeitenwandHinten.key
      ? userHeight -
        sidwallTyp.log.width / 2 -
        (hasBacksideStaticlog ? staticLogBackside.height - 0.01 : 0)
      : userHeight -
        height1 -
        (!hasStaticLog ? 0 : staticLogPosition + (sideWall.hasMarquee ? 0 : 0))

  const plankHeightTyp =
    sidewallContext[Keys.PlankenwandHeheTyp.Variable]?.key ?? 'default'

  const plankHeigtInput =
    plankHeightTyp === 'individuell'
      ? (sidewallContext['plankHeigtInput'] as unknown as number) / 100 || 0
      : userHeight

  let plankHeight =
    plankHeightTyp === 'default'
      ? (getAttribute(
          variables,
          sidewallContext,
          Keys.PlankHeight.Left,
          Keys.PlankHeight.LeftAttr
        ) as number) / 100
      : sideWall.hasWedge
      ? userHeight - Math.tan((userAngle * Math.PI) / 180) * depth - wedgeCut
      : spaceY

  plankHeight =
    plankHeigtInput > 0 && plankHeightTyp === 'individuell'
      ? plankHeigtInput
      : plankHeight

  const sidewallLeft = {
    logBarDepth: (sidewallContext['logBarDepth'] as unknown as number) / 100,
    plankHeight,
  }

  const sideWallMaxHeight =
    sideWallMaxHeight1 +
    (sideWall.hasPlankWall
      ? plankHeight + (plankWallWindow.hasTyp2 ? frameWall.frameHeight : 0)
      : 0) +
    (windowsWallTyp ? frameWall.frameHeight : 0)

  const spaceBetweenY =
    spaceY > sideWallMaxHeight
      ? sideWallMaxHeight
      : isQubus
      ? userHeight - (sideWall.hasPlankWall ? plankHeight : 0)
      : sidewallLeftRight && userHeight - 0.06 > sideWallMaxHeight
      ? spaceY
      : spaceY -
        (sideWall.hasWedge && sideWallType && !isWallCenter
          ? wedgeCut + (hasWedege ? wedgeLogWidth / 100 : logWidth / 100)
          : 0) -
        (sideWall.hasPlankWall ? plankHeight : 0)

  const cut =
    sideWall.hasWedge ||
    isWallCenter ||
    spaceY > sideWallMaxHeight ||
    isQubus ||
    (sidewallLeftRight && userHeight - 0.06 > sideWallMaxHeight)

  const height = isQubus
    ? spaceY < sideWallMaxHeight
      ? userHeight - spaceY - wedgeCut
      : userHeight - sideWallMaxHeight - wedgeCut - sidwallTyp.log.width
    : height1

  const wedgeCutter = isQubus ? height : (wedgeCut - offsetRoofLog.c / 2) / 2

  const verticalMarqueeColor =
    (sidewallContext['verticalMarqueeColor']?.value as string) ?? null

  const hasSidewallVsGlasAtt = (sidewallContext['seitenwandglas']?.attrs ??
    false) as boolean

  const wedgeMaterial = {
    glas: sidewallContext['keilMaterial']?.key === 'glas',
    poly: sidewallContext['keilMaterial']?.key === 'poly',
    plank: sidewallContext['keilMaterial']?.key === 'plank',
    default:
      sidewallContext['keilMaterial']?.key === 'glas' ||
      sidewallContext['keilMaterial']?.key === 'poly' ||
      sidewallContext['keilMaterial']?.key === 'plank'
        ? false
        : true,
  }

  const isSidwallVsGlas = hasSidewallVsGlasAtt
    ? (getAttribute(
        variables,
        sidewallContext,
        'seitenwandglas',
        'glas'
      ) as number) === 1
    : true

  const isPlankMaterial = wedgeMaterial.default
    ? isSidwallVsGlas
    : !wedgeMaterial.plank
    ? wedgeMaterial.glas
    : isSidwallVsGlas

  const doorHandle = {
    round: sidewallContext['griff']?.key === 'rund',
    shell: sidewallContext['griff']?.key === 'muschel',
    none: sidewallContext['griff']?.key === 'none',
    startEnd: sidewallContext['griff']?.key === 'startEnd',
    default:
      sidewallContext['griff']?.key === 'muschel' ||
      sidewallContext['griff']?.key === 'rund' ||
      sidewallContext['griff']?.key === 'startEnd' ||
      sidewallContext['griff']?.key === 'none'
        ? false
        : true,
  }

  const marqueeTyp = {
    rund: sidewallContext['verticalMarqueeTyp']?.key === 'rund',
    eckig: sidewallContext['verticalMarqueeTyp']?.key === 'eckig',
    ohne: sidewallContext['verticalMarqueeTyp']?.key === 'ohne',
    default:
      sidewallContext['verticalMarqueeTyp']?.key === 'rund' ||
      sidewallContext['verticalMarqueeTyp']?.key === 'ohne' ||
      sidewallContext['verticalMarqueeTyp']?.key === 'eckig'
        ? false
        : true,
  }

  const logBarSize =
    (!sideWall.hasWedge && plankHeight >= spaceY) || plankHeightTyp === 'full'
      ? {
          x: sidwallTyp.log.width,
          y: sidwallTyp.log.depth,
        }
      : {
          x: logBarDepth,
          y: sidewallLeftRight ? verandaTyp.log.width : verandaTyp.log.depth,
        }

  const wallGlasColor = (sidewallContext['wallGlasColor']?.value ||
    '#FFF') as ColorRepresentation

  const defaultMarqueeAttr = {
    width: spaceBetween,
    height: 0.1,
    depth: 0.1,
    radius: 0,
    position: verticalMarqueeInsideString,
  }

  const verticalMarqueeAttr = {
    height:
      (getAttribute(
        variables,
        sidewallContext,
        Keys.VerticalMarquee.Variable,
        Keys.VerticalMarquee.Attributes.Height
      ) as number) ?? defaultMarqueeAttr.height,
    depth:
      (getAttribute(
        variables,
        sidewallContext,
        Keys.VerticalMarquee.Variable,
        Keys.VerticalMarquee.Attributes.Width
      ) as number) ?? defaultMarqueeAttr.depth,
    position:
      (getAttribute(
        variables,
        sidewallContext,
        Keys.VerticalMarquee.Variable,
        Keys.VerticalMarquee.Attributes.Position
      ) as string) ?? defaultMarqueeAttr.position,
    width: defaultMarqueeAttr.width,
    radius: defaultMarqueeAttr.radius,
  }

  const verticalMarqueeMetalColor =
    ((sidewallContext['verticalMarqueeMetalColor']?.value ||
      color) as ColorRepresentation) ?? '#FFF'

  const slidingDoorPosition =
    ((sidewallContext['slidingDoorPos']?.value != '0') as boolean) || false

  const slidingPositionLeft =
    ((sidewallContext['slidingPosition']?.value != '0') as boolean) || false

  const doorTextureLabel = (sidewallContext['doorTexture']?.key ||
    'klar') as string

  const doorTexture = {
    opacity: getAttribute(
      variables,
      sidewallContext,
      'doorTexture',
      'opacity'
    ) as number,
    roughness: getAttribute(
      variables,
      sidewallContext,
      'doorTexture',
      'roughness'
    ) as number,
  }

  const frameDoorTyp = {
    typ1: sidewallContext['frameDoorTyp']?.key === 'typ1' || false,
    typ2: sidewallContext['frameDoorTyp']?.key === 'typ2' || false,
  }

  const shutterHeight =
    (getAttribute(
      variables,
      sidewallContext,
      Keys.SeitenwandTyp.Variable,
      'shutterWidth'
    ) as number) ?? 0

  const shutterDepth =
    (getAttribute(
      variables,
      sidewallContext,
      Keys.SeitenwandTyp.Variable,
      'glas'
    ) as number) ?? 0

  const hasSlidingRail =
    sidewallContext['slidingRail']?.value !== '0' ? true : false

  const shutter = {
    height: shutterHeight > 0 ? shutterHeight / 100 : 0.1,
    depth: shutterDepth > 0 ? shutterDepth / 100 : 0.02,
    color: (sidewallContext['shutterColor']?.value ||
      sidewallContext['shutterColor']?.key) as ColorRepresentation,
    count: (sidewallContext['shutterAnzahl'] as unknown as number) || 1,
    sliding: (hasSlidingRail || false) as boolean,
  }

  return {
    sideWall,
    sideWallType,
    windowsWallTyp,
    plankWallWindow,
    frameWall,
    sidewallLeft,
    plankHeightTyp,
    plankHeight,
    logBarDepth,
    logBarSize,
    brushTyp,
    triangularDivider,
    wedgeColor,
    sidwallTyp,
    type: sidewallContext.type,
    sidewallIndex: sidewallContext.index,
    sideWallMaxHeight,
    sidewallId: sidewallContext.id,
    windowsSideWidth,
    height,
    spaceBetween,
    windowsSideTotal,
    position,
    basicPosition,
    basicSpaceBetween,
    rotation,
    wedgeCut,
    spaceY,
    spaceBetweenY,
    verticalMarqueeColor,
    hasVerticalMarquee,
    isSidwallVsGlas,
    isPlankMaterial,
    doorHandle,
    marqueeTyp,
    cut,
    wedgeCutter,
    windowColorWedge,
    wedgeMaterial,
    plankHeigtInput,
    isWallCenter,
    verticalMarqueeInside,
    checkDefaultVerticalMarquee,
    verticalMarqueeAttr,
    wallGlasColor,
    verticalMarqueeMetalColor,
    sideWallVariable,
    sideWallValue,
    sideTyp,
    hasSideWall,
    windowsSideMaxWidth,
    slidingDoorPosition,
    slidingPositionLeft,
    doorTexture,
    frameDoorTyp,
    shutter,
    hasWedege,
    wedgeLogWidth,
    wedgeLogDepth,
  }
}
